import { useCallback, type RefObject } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { useSelectedGcsConfigsStore } from '../store/useSelectedGcsConfigsStore';
import { useSelectedVehicleStore } from '../store/useSelectedVehicleStore';
import { useGcsStore } from '../store/useGcsStore';
import { useMissionVisibilityStore } from '../store/useMissionVisibilityStore';
import { fitMapToCoordinates, getCenterFocusCoordinates } from '../utils/map';

/**
 * Focuses the map on all relevant operational entities across all selected centers:
 * control zone, launchpads, live mission pickup/delivery points,
 * mission routes, failure-mode routes/safe points, drones and foreign aircraft.
 */
export const useFocusMap = (mapRef: RefObject<MapRef | null>) => {
  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);
  const missionVisibility = useMissionVisibilityStore((state) => state.missionVisibility);

  return useCallback(() => {
    if (!mapRef.current) {
      return;
    }
    const selectedGcsConfigs = useSelectedGcsConfigsStore.getState().selectedConfigs;
    const gcsDataMap = useGcsStore.getState().data;

    const coordinates: Array<[number, number]> = [];

    selectedGcsConfigs.forEach((config) => {
      const gcsData = gcsDataMap.get(config.id);
      if (!gcsData) return;

      const visibleMissionIds = new Set(
        Array.from(gcsData.liveMissionsMap.values())
          .filter(
            (mission) =>
              missionVisibility === 'all' ||
              (selectedVehicle?.gcsId === config.id && mission.vehicleId === selectedVehicle.vehicleId)
          )
          .map((mission) => mission.missionId)
      );

      coordinates.push(...getCenterFocusCoordinates(gcsData, visibleMissionIds));
    });

    if (coordinates.length === 0) {
      return;
    }

    fitMapToCoordinates(mapRef.current, coordinates, !!selectedVehicle);
  }, [mapRef, missionVisibility, selectedVehicle]);
};
